import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../../shared/api/firebase/config';

/* ============================================================
   IngestionHubRepository
   Encapsula TODAS as chamadas Firestore diretas do IngestionHub.
   ============================================================ */

export const getClientRef = (appId, clientId) =>
  doc(db, 'artifacts', appId, 'public', 'data', 'clients', clientId);

// Fusão Processos/Caderno (MC35): processes vazia - redireciona. O nome
// "Matter" ja indicava a intencao antes desta fusao.
export const getMatterRef = (appId, matterId) =>
  doc(db, 'artifacts', appId, 'public', 'data', 'autos_workspaces', matterId);

export const getDocumentRef = (appId, docId) =>
  doc(db, 'artifacts', appId, 'public', 'data', 'documents', docId);

export const getDocumentRequestRef = (taskRef) =>
  doc(db, 'document_requests', taskRef);

export const getTriageRef = (triageId, existingRef = null) =>
  existingRef || doc(db, 'rda_triage', triageId);

export const getArtifactsDocumentsCollection = (appId) =>
  collection(db, 'artifacts', appId, 'public', 'data', 'documents');

export const getRootDocumentsCollection = () =>
  collection(db, 'documents');

export const getWorkspacesCollection = (appId) =>
  collection(db, 'artifacts', appId, 'public', 'data', 'autos_workspaces');

export const getClientNotesCollection = (appId, clientId) =>
  collection(db, 'artifacts', appId, 'public', 'data', 'clients', clientId, 'notes');

export const getTriageCollection = () =>
  collection(db, 'rda_triage');

export const getArtifactsTriageCollection = (appId) =>
  collection(db, 'artifacts', appId, 'public', 'data', 'rda_triage');

/* ---------- Leitura simples ---------- */

export const fetchDocument = async (ref) => {
  const snap = await getDoc(ref);
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const fetchClientById = (appId, clientId) =>
  fetchDocument(getClientRef(appId, clientId));

export const fetchMatterById = (appId, matterId) =>
  fetchDocument(getMatterRef(appId, matterId));

/* ---------- Workspace ---------- */

export const findWorkspaceByMatterId = async (appId, matterId) => {
  const workspacesRef = getWorkspacesCollection(appId);
  const workspaceSnap = await getDocs(
    query(workspacesRef, where('matterId', '==', matterId), limit(1))
  );
  const workspaceDoc = workspaceSnap.docs[0] || null;
  const workspaceRef = workspaceDoc?.ref || doc(workspacesRef);
  return { workspaceDoc, workspaceRef, workspaceData: workspaceDoc?.data() || {} };
};

export const upsertWorkspace = async (workspaceRef, payload, isNew) => {
  const data = { ...payload, updatedAt: serverTimestamp() };
  if (isNew) data.createdAt = serverTimestamp();
  await setDoc(workspaceRef, data, { merge: true });
};

/* ---------- Cliente (Drive) ---------- */

export const updateClientDriveData = async (appId, clientId, driveData) => {
  const clientRef = getClientRef(appId, clientId);
  await setDoc(
    clientRef,
    {
      driveFolderId: driveData.driveFolderId,
      driveFolders: driveData.driveFolders || null,
      driveTreeVersion: driveData.driveTreeVersion,
      driveTreeHealthy: driveData.driveTreeHealthy,
      driveTreeLastCheckedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
};

/* ---------- Notas do cliente ---------- */

export const writeClientNote = async ({ appId, clientId, type, actorName, payload }) => {
  const safeClientId = String(clientId || '').trim();
  const safeAppId = String(appId || '').trim();
  if (!safeClientId || !safeAppId) return;

  await addDoc(getClientNotesCollection(safeAppId, safeClientId), {
    category: 'SYSTEM',
    type,
    actor: {
      name: String(actorName || '').trim() || 'Escritório',
      isBot: false,
    },
    payload,
    createdAt: serverTimestamp(),
  });
};

/* ---------- Orphan documents ---------- */

export const loadOrphanDocuments = async (appId) => {
  const docsRef = getArtifactsDocumentsCollection(appId);
  const [nullSnap, emptySnap] = await Promise.all([
    getDocs(query(docsRef, where('clientId', '==', null), limit(300))),
    getDocs(query(docsRef, where('clientId', '==', ''), limit(300))),
  ]);

  const merged = new Map();
  [...nullSnap.docs, ...emptySnap.docs].forEach((d) => {
    const data = d.data() || {};
    const clientId = data?.clientId;
    const matterId = data?.matterId;
    if (clientId !== null && clientId !== '' && matterId !== null && matterId !== '') return;
    merged.set(String(d.id), { id: `doc:${d.id}`, sourceKind: 'document_orphan', sourceRefId: d.id, ...data });
  });

  return Array.from(merged.values()).sort((a, b) => {
    const toMillis = (v) => {
      if (v?.toDate) return v.toDate().getTime();
      const d = new Date(v || 0);
      return Number.isNaN(d.getTime()) ? 0 : d.getTime();
    };
    return toMillis(b?.createdAt) - toMillis(a?.createdAt);
  });
};

/* ---------- Portal uploads (real-time) ---------- */

export const subscribePortalUploads = (appId, onSuccess, onError) => {
  const buildQuery = (ref) =>
    query(ref, where('status', '==', 'pending_review'), limit(200));

  const makeHandler = () => (snap) => {
    const rows = snap.docs.map((d) => ({
      id: `portal:${d.id}`,
      sourceKind: 'portal_upload',
      sourceRefId: d.id,
      ...(d.data() || {}),
      _ref: d.ref,
    }));
    onSuccess(rows);
  };

  let unsub = null;

  const subscribe = (ref, failHandler) => {
    return onSnapshot(buildQuery(ref), makeHandler(), failHandler);
  };

  unsub = subscribe(getTriageCollection(), (e) => {
    const baseMsg = String(e?.message || e || 'Falha ao carregar a fila do portal.');
    try { unsub && unsub(); } catch (err) { /* no-op */ }

    const safeAppId = String(appId || '').trim();
    if (!safeAppId) {
      onError(baseMsg);
      return;
    }

    unsub = subscribe(getArtifactsTriageCollection(safeAppId), (e2) => {
      onError(String(e2?.message || e2 || baseMsg));
    });
  });

  return () => {
    try { unsub && unsub(); } catch (e) { /* no-op */ }
  };
};

/* ---------- Document requests ---------- */

export const updateDocumentRequest = async (taskRef, data) => {
  const ref = getDocumentRequestRef(taskRef);
  await setDoc(ref, { ...data, updatedAt: serverTimestamp() }, { merge: true });
};

/* ---------- Rejeição de portal (batch) ---------- */

export const rejectPortalUpload = async ({ triageRef, taskRef, cleanReason }) => {
  const batch = writeBatch(db);

  batch.update(triageRef, {
    status: 'rejected',
    updatedAt: serverTimestamp(),
    rejectionReason: cleanReason,
  });

  if (taskRef) {
    batch.set(
      getDocumentRequestRef(taskRef),
      {
        status: 'pending',
        rejectionReason: cleanReason,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  }

  await batch.commit();
};

/* ---------- Arquivamento no GED (batch) ---------- */

export const buildGedBatch = (gedCollectionRef, documentPayload, triageRef, context) => {
  const batch = writeBatch(db);
  const gedRef = doc(gedCollectionRef);
  batch.set(gedRef, documentPayload);

  if (triageRef) {
    batch.update(triageRef, {
      status: 'approved',
      updatedAt: serverTimestamp(),
      documentId: gedRef.id,
      clientId: context.clientId || null,
      clientName: context.clientName || null,
      matterId: context.matterId || null,
      matterName: context.matterName || null,
      workspaceId: context.workspaceId || null,
      archivedFolderId: context.archivedFolderId || null,
      fileUrl: context.fileUrl,
      fileName: context.fileName,
      storageEngine: context.storageEngine || null,
    });
  }

  return { batch, documentId: gedRef.id };
};

export const commitGedBatch = async (gedCollectionRef, documentPayload, triageRef, context) => {
  const { batch, documentId } = buildGedBatch(gedCollectionRef, documentPayload, triageRef, context);
  await batch.commit();
  return documentId;
};

/* ---------- WhatsApp / Documentos diretos ---------- */

export const addWhatsAppDocument = async (appId, payload) => {
  const docsRef = getArtifactsDocumentsCollection(appId);
  await addDoc(docsRef, payload);
};

/* ---------- Update orphan/doc vinculado ---------- */

export const updateDocumentVinculo = async (appId, sourceDocId, updateData) => {
  const targetRef = getDocumentRef(appId, sourceDocId);
  await updateDoc(targetRef, {
    ...updateData,
    updatedAt: serverTimestamp(),
  });
};

/* ---------- Batch / Doc helpers ---------- */

export const createBatch = () => writeBatch(db);

export const docFromCollection = (collectionRef) => doc(collectionRef);

/* ---------- serverTimestamp / arrayUnion re-exportados ---------- */
export { serverTimestamp, arrayUnion };
